/* eslint-disable i18next/no-literal-string -- a code-only module: every literal is a phase, outcome or storage code, a key or an event name; it renders nothing */
// The deck editor's save queue: every edit is saved on its own, a moment
// after it is made, without re-analyzing (plan 2026-10-15, web release 1, 1b).
//
// A MODULE-LEVEL controller per (user | guest, deck), not React state, so a
// write in flight outlives the screen that started it. The `saveDeckChecked`
// it writes through is the one the CURRENTLY ATTACHED screen injected (never a
// second useSavedDecks() instance of its own; DeckDetailV2's saveDeck rule).
//
// Lifecycle
//  - attach(args) returns a token; detach(token) on unmount. A callback is
//    only ever delivered to the args attached NOW, so an old screen's
//    callbacks are dropped. A settled, detached controller is evicted.
//  - attach() replays the last durable row through the new instance's
//    applyPersistedDeck, so a remount's fresh list and the detail both show
//    it without a reload.
//  - After a detach, automatic retries wait for the next attach. An explicit
//    flush (sign-out, page unload) uses the last attached args.
//
// Writes
//  - Debounced (SAVE_DEBOUNCE_MS) and single-flight.
//  - Compare-and-swap on `lastDurableText` (the stored text this controller
//    last saw land), with keepAnalysis, updateOnly and rollbackOnFailure.
//    A write whose text moved the command zone also writes the row's
//    commander / partner in that same update (commandZoneSwap.zoneWriteFor).
//    The per-card printing / foil / tags (card_meta, Wave 4) are tracked
//    beside the text: edit(text, meta) records them, and a write sends them
//    (pruned to the cards its text holds) in that same update whenever they
//    differ from what this controller last saw stored. A text-only edit that
//    drops a card with an entry therefore prunes it on its write.
//    card_meta is ALWAYS sent as a MERGE ({base, next}: the cards the
//    session changed since `metaBase`, the map it opened on), which the save
//    applies to a FRESH read of the row's card_meta in the same
//    compare-and-swap, so another client's change to any other card is never
//    replaced by this session's stale copy. Known stored value: a text edit
//    that drops a card with an entry sends an empty merge, which the save
//    prunes. UNKNOWN stored value (a row loaded by a path that never read
//    the column): pruning alone sends nothing.
//    A draft carries card_meta only when the session actually changed it,
//    with the base it was measured from, so a restore re-applies the delta.
//  - saved: lastDurableText moves; IN THE SAME synchronous step the draft is
//    rewritten to {base: the saved text, text: the newer pending text}, or
//    cleared when nothing is pending; the screen hears the stored row.
//  - saved with cardMetaHeld (the list landed, card_meta could not be sent):
//    the list is durable, the card_meta edits stay pending in the draft and
//    the queue stops as fatal ('invalid'); Try again re-sends them.
//  - stale / missing / fatal: the queue stops and keeps the draft. A
//    card_meta merge whose field the stored row ALSO moved is stale too.
//  - retryable: backs off 1s, 2s, 4s ... capped at 30s.
//  - Every write is a compare-and-swap on the row's rev too (`lastDurableRev`,
//    the rev this controller last saw stored; unknown: the save reads the
//    row fresh first), and adopts the rev the database returns.
//  - unknown (a lost response, see useSavedDecks UnknownSaveAttempt): the
//    attempt is kept EXACTLY as sent, IN MEMORY, and resolved before anything
//    newer is written (saveDeckChecked with resolveUnknown, on the same
//    backoff). Newer edits are recorded and drafted meanwhile, an undo back to
//    the base included; nothing reports saved while it is unresolved, and a
//    controller holding one is never evicted. Resolved saved: its text is the
//    durable base and newer edits are written next; stale: the queue stops
//    and keeps the draft. A rejected identical retry (signed out) stops the
//    queue as fatal but KEEPS the attempt: Try again resolves it first.
//  - Every write carries the rev AND command zone of the stored row its base
//    text came from (expectedRev / expectedCommandZone); a session that
//    never learned the rev sends 'fresh' (an instant save may establish it).
//  - Journaled before dispatch: the draft is marked `uncertain` BEFORE every
//    write goes out (the queue's own and every external full save routed
//    through runExternal: re-rates, re-analyses, tool applies), and the mark
//    is cleared only once that write's outcome is settled (saved, stale,
//    missing, fatal, or retryable: a request that did not run). Sign-out and
//    unload flushes wait for external saves too.
//  - Accepting an uncertain recovery draft FORCES a compare-and-swap write
//    (edit(..., { force: true })), even of the text the row already holds:
//    the no-op update bumps rev, so an older write still in flight can no
//    longer land. It counts as pending until that write is saved.
//  - A stale / missing stop KEEPS the draft (even an undo back to the old
//    base) and counts as pending work until the user dismisses it (Discard)
//    or it is saved, so sign-out never reports it saved. A reload therefore finds any write that may have landed
//    unconfirmed: that draft is always OFFERED as the ordinary recovery prompt
//    (never cleared as redundant, never replayed), its card_meta whole.
//
// Drafts
//  - After EVERY edit, synchronously: localStorage[draftKey] =
//    {base: lastDurableText, text, ts}. `draftPersisted` says whether that
//    write landed; when it did not (full or blocked storage) the page's
//    unload guard stays armed on a failure.
import { ownerStorage } from '../../../lib/offline/ownerStorage'
import type { CompleteAnalysis } from '../../../api/types'
import type { SaveDeckCheckedFn, SavedDeck, SaveFatalCode, SaveOutcome, UnknownSaveAttempt } from '../../../hooks/useSavedDecks'
import { zoneWriteFor } from './commandZoneSwap'
import { canonicalCardMeta, parseCardMeta, pruneCardMetaForText, type CardMetaMap } from '../../../lib/cardMeta'

export const SAVE_DEBOUNCE_MS = 600

/** What a write sends for card_meta (see metaWriteFor). */
type MetaWrite = { cardMetaMerge: { base: CardMetaMap; next: CardMetaMap }; cardMetaNoPrune?: true }

/** A lost write held (in memory) until it is resolved: the attempt, the text
 *  it sent and its card_meta merge (so a resolved save moves the bases
 *  exactly as a normal one would). */
interface PendingUnknown {
  attempt: UnknownSaveAttempt
  text: string
  meta?: MetaWrite
}

const BACKOFF_BASE_MS = 1000
const BACKOFF_CAP_MS = 30_000

// 'offline': the edit is durable in the offline deck outbox (offline
// contract C4.6) and is sent by the sync scheduler when the account is back.
export type QueuePhase = 'idle' | 'pending' | 'saving' | 'retrying' | 'stale' | 'missing' | 'fatal' | 'offline'

/**
 * The offline deck outbox, as the save queue sees it (installed by
 * src/lib/offline/deckOutboxBridge.ts; absent: today's online-only queue).
 * While the API is unreachable, the session is not usable, or the outbox
 * still holds this deck's edit, a write is RECORDED in the outbox (one
 * writer per deck) instead of sent; the scheduler sends it.
 */
export interface EditorOfflinePort {
  shouldQueue: (scope: string, deckId: string) => boolean
  /** C2: false while the session is rejected (read-only): no edit, no draft change (finding 2). */
  canEdit: (scope: string) => boolean
  /** Retries are the scheduler's while offline (C1b); the queue's own timer waits. */
  isOffline: () => boolean
  recordEdit: (input: {
    deckId: string
    baseRev: number | null
    baseText: string | null
    baseCardMeta?: unknown
    text: string
    cardMetaDelta: { base: CardMetaMap; next: CardMetaMap; no_prune?: boolean } | null
  }) => Promise<'queued' | 'cleared' | 'read_only' | 'unavailable' | 'refused'>
  /** The user threw the edit away: the outbox's copy goes too. */
  discard: (deckId: string) => Promise<void>
}

let offlinePort: EditorOfflinePort | null = null

export function setEditorOfflinePort(port: EditorOfflinePort | null): void {
  offlinePort = port
}
export type FlushResult = 'saved' | 'stale' | 'missing' | 'retryable_error' | 'fatal_error' | 'held'

export interface QueueSnapshot {
  phase: QueuePhase
  fatalCode: SaveFatalCode | null
  /** The last draft write landed (the edits survive a reload): true only after its COMMIT. */
  draftPersisted: boolean
  /** A draft write is accepted but its commit is still out (not durable yet, not failed either). */
  draftCommitting?: boolean
  /** The editor holds text the database does not have yet. */
  hasPending: boolean
  lastDurableText: string | null
  /** A Discard's fence is out (or held unknown): every recovery action and
   *  document edit is refused until it settles. */
  fencing?: boolean
  /**
   * Work is pending but NOTHING will run on its own: no editor attached, no
   * write in flight and no debounce or backoff timer (automatic writes and
   * retries need an attached editor; the next attach or an explicit flush
   * resumes them).
   */
  paused?: boolean
  /** A write may have landed unconfirmed (one out, or a lost one held). */
  uncertainWrite?: boolean
}

export interface QueueAttachArgs {
  saveDeckChecked: SaveDeckCheckedFn
  /** useSavedDecks' applyPersistedDeck of the attaching hook instance. */
  applyPersistedDeck?: (deck: SavedDeck) => void
  /** The stored row after each landed write (the detail's override). */
  onDeckUpdated?: (deck: SavedDeck) => void
  /** Passed through to the write; an instant save keeps the stored one. */
  analysis: () => CompleteAnalysis
  partner: () => string | null
}

export interface EditorDraft {
  base: string | null
  text: string
  ts: number
  /** The editor's card_meta when the session edited it (absent: untouched). */
  meta?: CardMetaMap
  /** The map that edit was measured from (restore applies meta minus this). */
  metaBase?: CardMetaMap
  /** A write was dispatched and its outcome was not settled when this draft
   *  was written (it may have landed). Such a draft is always offered, never
   *  cleared as redundant or replayed, and its meta is the session's whole
   *  map (`meta` replaces, it is not a delta over `metaBase`). */
  uncertain?: true
}

const DRAFT_PREFIX = 'cb_editor_draft:'
// The WRITER id of this document: random, generated once per page load, in
// memory only. A draft key is only ever written or cleared by the document
// that created it, so two documents (tabs, a duplicated tab, a reload) never
// share a key. A reload leaves the old document's draft under its old id,
// where the open-time recovery scan (listDrafts) finds and offers it.
const freshId = () => Math.random().toString(36).slice(2, 10)
let documentWriterId = freshId()

// The retired per-tab id (2026-10 web release 1 drafts): no longer read.
try {
  if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem('cb_editor_tab_id')
} catch {
  // No sessionStorage: nothing to remove.
}

/** This document's writer id. */
export function tabId(): string {
  return documentWriterId
}

/** Test seam: act as another document. */
export function __setTabIdForTests(id: string): void {
  documentWriterId = id
}

/** This document's draft key for the deck. */
export function draftKey(scope: string, deckId: string, tab: string = tabId()): string {
  return `${DRAFT_PREFIX}${scope}:${deckId}:${tab}`
}

function parseDraft(raw: string | null): EditorDraft | null {
  if (!raw) return null
  try {
    const d = JSON.parse(raw) as Partial<EditorDraft>
    if (typeof d.text !== 'string') return null
    const out: EditorDraft = { base: typeof d.base === 'string' ? d.base : null, text: d.text, ts: typeof d.ts === 'number' ? d.ts : 0 }
    // Lenient, like the column: a malformed meta is no meta.
    // card_meta only WITH the base it was measured from: a draft from before
    // the delta rule (a whole map, no base) restores its text only, since
    // its map may be stale against the row.
    const isMap = (v: unknown) => !!v && typeof v === 'object' && !Array.isArray(v)
    if (isMap(d.meta) && isMap(d.metaBase)) {
      out.meta = parseCardMeta(d.meta)
      out.metaBase = parseCardMeta(d.metaBase)
    }
    if ((d as { uncertain?: unknown }).uncertain === true) out.uncertain = true
    return out
  } catch {
    return null
  }
}

/** THIS document's draft for the deck, or null (none, unreadable or malformed). */
export function readDraft(scope: string, deckId: string): EditorDraft | null {
  try {
    return parseDraft(ownerStorage.getItem(draftKey(scope, deckId)))
  } catch {
    return null
  }
}

/** Every document's draft for this deck (and a pre-writer-id one), newest first. */
export function listDrafts(scope: string, deckId: string): { key: string; draft: EditorDraft }[] {
  const out: { key: string; draft: EditorDraft }[] = []
  const exact = `${DRAFT_PREFIX}${scope}:${deckId}`
  try {
    for (const k of ownerStorage.keys()) {
      if (!k || (k !== exact && !k.startsWith(`${exact}:`))) continue
      const draft = parseDraft(ownerStorage.getItem(k))
      if (draft) out.push({ key: k, draft })
    }
  } catch {
    // Blocked storage holds no drafts.
  }
  return out.sort((a, b) => b.draft.ts - a.draft.ts)
}

/** Remove ONE draft key (the one acted on). */
export function clearDraftKey(key: string): void {
  try {
    ownerStorage.removeItemUnconfirmed(key)
  } catch {
    // Blocked storage: there is nothing to clear.
  }
}

/** Remove THIS document's draft for the deck. */
export function clearDraft(scope: string, deckId: string): void {
  clearDraftKey(draftKey(scope, deckId))
}

/**
 * After a COMPLETED, successful load of the scope's whole list: drop the
 * drafts of decks the load confirmed are gone (deleted elsewhere). Drafts are
 * otherwise deleted only when redundant on open, discarded, restored, made
 * durable by their own document, or with the account; never by age, since a
 * draft can be the only copy of an edit.
 */
export function pruneDraftsForMissingDecks(scope: string, presentDeckIds: readonly string[], loadStartedAt: number): void {
  const present = new Set(presentDeckIds)
  const prefix = `${DRAFT_PREFIX}${scope}:`
  for (const key of draftKeysForScope(scope)) {
    const deckId = key.slice(prefix.length).split(':')[0]
    if (!deckId || present.has(deckId)) continue
    // Only a draft OLDER than the load that proved the deck missing: one
    // written at or after it started may belong to a deck created meanwhile.
    let ts = Number.POSITIVE_INFINITY
    try {
      ts = parseDraft(ownerStorage.getItem(key))?.ts ?? Number.POSITIVE_INFINITY
    } catch {
      // Unreadable: keep it.
    }
    if (ts < loadStartedAt) clearDraftKey(key)
  }
}

let recoverySeq = 0

/**
 * Preserve a stale tool apply's result as its OWN draft: a fresh key per
 * apply (this document's writer id plus a recovery suffix), never the
 * editor's draft key and never an existing draft, so a second unresolved
 * apply cannot overwrite the first. Returns the key, or null when storage
 * refused the write. The key is ACCEPTED, not yet durable: `onLost` runs if
 * its commit then fails (the caller keeps the result in memory instead).
 */
export function writeRecoveryDraft(scope: string, deckId: string, draft: EditorDraft, onLost?: () => void): string | null {
  let key: string
  do {
    recoverySeq += 1
    key = `${draftKey(scope, deckId)}-r${recoverySeq}${Math.random().toString(36).slice(2, 6)}`
  } while ((() => { try { return ownerStorage.getItem(key) !== null } catch { return false } })())
  try {
    const w = ownerStorage.write(key, JSON.stringify(draft))
    if (!w.accepted) return null
    if (typeof w.durable === 'boolean') return w.durable ? key : null
    void w.durable.then((ok) => { if (!ok) onLost?.() })
    return key
  } catch {
    return null
  }
}

/**
 * Guest -> account migration: move every DIVERGENT guest draft of a migrated
 * deck to the account's scope and the deck's new cloud id (keeping each
 * draft's writer suffix), rebased on the migrated decklist so recovery offers
 * a plain Restore after sign-in. A guest key is removed only after its copy
 * was written. Drafts identical to the migrated text are left alone.
 */
export async function rekeyMigratedDrafts(fromDeckId: string, toScope: string, toDeckId: string, migratedText: string | null): Promise<boolean> {
  const from = `${DRAFT_PREFIX}guest:${fromDeckId}`
  let all = true
  for (const { key, draft } of listDrafts('guest', fromDeckId)) {
    // Identical to what was migrated: redundant, so it simply goes (unless
    // it carries card_meta edits, which the migrated row may not hold).
    if (draft.text === (migratedText ?? '') && !draft.meta) {
      clearDraftKey(key)
      continue
    }
    const suffix = key.length > from.length ? key.slice(from.length + 1) : tabId()
    const to = `${DRAFT_PREFIX}${toScope}:${toDeckId}:${suffix}`
    // The exact version copied: the source goes only if it still holds it.
    let raw: string | null = null
    try {
      raw = ownerStorage.getItem(key)
    } catch {
      raw = null
    }
    // The guest source goes only after the destination COMMITTED (r2 #1):
    // until then it is the edit's recovery copy.
    let durable: boolean
    try {
      const w = ownerStorage.write(to, JSON.stringify({ base: migratedText, text: draft.text, ts: draft.ts, ...(draft.meta ? { meta: draft.meta, metaBase: draft.metaBase ?? {} } : {}) }))
      durable = w.accepted && await w.durable
    } catch {
      durable = false
    }
    if (!durable || raw === null) {
      all = false
      continue
    }
    // Changed while the copy committed (r3 blocker 3): the newer guest text
    // stays, and the migration reports incomplete so the guest deck stays.
    let removed = false
    try {
      removed = await ownerStorage.removeIfEquals(key, raw)
    } catch {
      removed = false
    }
    if (!removed) all = false
  }
  return all
}

/** Every draft key of one user (account deletion clears them). */
export function draftKeysForScope(scope: string): string[] {
  const out: string[] = []
  try {
    for (const k of ownerStorage.keys()) {
      if (k && k.startsWith(`${DRAFT_PREFIX}${scope}:`)) out.push(k)
    }
  } catch {
    // Blocked storage holds no drafts.
  }
  return out
}

export class EditorSaveController {
  readonly scope: string
  readonly deckId: string
  private lastDurableText: string | null
  // saved_decks.rev of the stored row lastDurableText came from (undefined:
  // unknown, the save reads the row fresh first).
  private lastDurableRev: number | undefined
  // A lost write, held until resolved (see the header).
  private unknown: PendingUnknown | null = null
  // REFRESH-OWNED state (a linked-source refresh journaled here through
  // runExternal(..., { refresh: true })): the held lost write is the
  // refresh's, and latestText holds its pulled text, NOT an edit the user
  // made. A definitive stale / missing resolution retires that state (the
  // pulled version lives on only as the deck's refresh choice), and
  // editorWorkPending() never counts it as editor work. An edit() hands
  // latestText back to the editor.
  private unknownRefreshOwned = false
  private textRefreshOwned = false
  private refreshExternals = 0
  // How a retired refresh-owned write resolved, for the flush that ran it.
  private retiredAs: 'stale' | 'missing' | null = null
  // The command zone (commander / partner columns) of the stored row
  // lastDurableText came from (undefined: unknown).
  private lastDurableZone: { commander: string | null; partner: string | null } | undefined
  // Writes dispatched whose outcome is not settled: the queue's own and
  // external full saves (runExternal). The draft is marked uncertain while
  // any is out or a lost one is held.
  private dispatched = 0
  private externals = new Set<Promise<unknown>>()
  // An accepted uncertain recovery: one CAS write is owed even if the text
  // is the stored one (see the header).
  private forceWrite = false
  // The owed write is the FENCE itself (fence()): a no-op of the list column
  // at the base rev, nothing else sent; a list-less legacy row stays list-less.
  private fenceOnly = false
  // Stopped stale / missing with the user's edit kept: pending until
  // dismissed or saved.
  private unsavedEdit = false
  // The newest edit is durable in the offline deck outbox (phase 'offline').
  // While set, the outbox holds this deck's edit: every later edit (an undo
  // back to the old base included) is recorded there too (finding 4).
  private queuedOffline = false
  // Every edit() bumps it; a hand-off that captured an older one is followed
  // by another (an edit made while recordEdit awaited, finding 5).
  private editGen = 0
  // The editor's newest text; null until the first edit.
  private latestText: string | null = null
  // card_meta as this controller last saw it stored (canonicalCardMeta;
  // '' = none), or undefined when UNKNOWN (merge mode, see the header).
  private durableMeta: string | undefined
  // The session opened on an UNKNOWN stored map: it never prunes the row's
  // entries (not even after a save's response tells it the stored value),
  // only applies its own delta. Its Undo could not bring a pruned entry back.
  private noPrune: boolean
  // Merge mode: the map the session's edits are measured from.
  private metaBase: CardMetaMap = {}
  // The editor's newest card_meta (unpruned); null until an edit carries one.
  private latestMeta: CardMetaMap | null = null
  private lastDurableDeck: SavedDeck | null = null
  private phase: QueuePhase = 'idle'
  private fatalCode: SaveFatalCode | null = null
  private draftPersisted = true
  // The owner DB commits asynchronously: draftPersisted turns true only when
  // the newest draft write committed (r2 #1); older results are ignored.
  private draftCommitting = false
  private draftSeq = 0
  private inFlight: Promise<SaveOutcome['status'] | null> | null = null
  private debounceTimer: ReturnType<typeof setTimeout> | null = null
  private retryTimer: ReturnType<typeof setTimeout> | null = null
  private retryAttempt = 0
  // hold(): automatic writes wait while > 0 (a re-rate is saving).
  private holds = 0
  private attached: QueueAttachArgs | null = null
  private lastArgs: QueueAttachArgs | null = null
  private token = 0
  private listeners = new Set<() => void>()
  private snap: QueueSnapshot
  private readonly onSettled: (c: EditorSaveController) => void

  constructor(init: {
    scope: string
    deckId: string
    storedDecklist: string | null
    /** The row's card_meta as loaded (raw; read leniently). Absent: unknown. */
    storedCardMeta?: unknown
    /** The row's rev, read with storedDecklist. Absent: unknown. */
    storedRev?: number
    /** The row's commander / partner, read with storedDecklist. */
    storedZone?: { commander: string | null; partner: string | null }
    onSettled: (c: EditorSaveController) => void
  }) {
    this.scope = init.scope
    this.deckId = init.deckId
    this.lastDurableText = init.storedDecklist
    this.lastDurableRev = init.storedRev
    this.lastDurableZone = init.storedZone
    this.durableMeta = init.storedCardMeta !== undefined ? canonicalCardMeta(parseCardMeta(init.storedCardMeta)) : undefined
    this.noPrune = init.storedCardMeta === undefined
    this.onSettled = init.onSettled
    this.snap = this.buildSnapshot()
  }

  // ---- React subscription --------------------------------------------------

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn)
    return () => { this.listeners.delete(fn) }
  }

  getSnapshot = (): QueueSnapshot => this.snap

  private buildSnapshot(): QueueSnapshot {
    return {
      phase: this.phase,
      fatalCode: this.fatalCode,
      draftPersisted: this.draftPersisted,
      draftCommitting: this.draftCommitting,
      hasPending: this.hasPendingWork(),
      lastDurableText: this.lastDurableText,
      fencing: this.fenceOnly,
      paused: !this.attached && this.inFlight === null && this.debounceTimer === null && this.retryTimer === null && this.hasPendingWork(),
      uncertainWrite: this.uncertain,
    }
  }

  private notify() {
    this.snap = this.buildSnapshot()
    for (const fn of [...this.listeners]) fn()
  }

  // ---- State ---------------------------------------------------------------

  /** The text the database holds, as far as this controller knows. */
  durableText(): string | null {
    return this.lastDurableText
  }

  /** The rev of that stored text (undefined: unknown). */
  durableRev(): number | undefined {
    return this.lastDurableRev
  }

  /** The command zone of that stored row (undefined: unknown). */
  durableZone(): { commander: string | null; partner: string | null } | undefined {
    return this.lastDurableZone
  }

  // A write may have landed unconfirmed (one is out, or a lost one is held),
  // or a forced CAS is OWED (an accepted uncertain recovery: until it is
  // saved, an older write from before the reload may still land).
  private get uncertain(): boolean {
    return this.dispatched > 0 || this.unknown !== null || this.forceWrite
  }

  /** A Discard's fence is pending or unknown: edits are refused (edit()). */
  isFencing(): boolean {
    return this.fenceOnly
  }

  /** A lost write is held, not yet resolved. */
  hasUnknownAttempt(): boolean {
    return this.unknown !== null
  }

  /**
   * Work of the EDITOR's (or of a re-rate / tool apply) that a refresh must
   * not write past: a fence, a dirty or stopped edit, a write or timer out,
   * a lost write, an external save. A linked-source refresh's own state
   * (see unknownRefreshOwned) is not counted.
   */
  editorWorkPending(): boolean {
    if (this.fenceOnly) return true
    const refreshOnly = this.textRefreshOwned && (!this.unknown || this.unknownRefreshOwned)
    if (refreshOnly) {
      // Everything queued is the refresh's (its held attempt, the resolve
      // and the backoff that serve it); only a non-refresh external counts.
      return this.externals.size > this.refreshExternals
    }
    return this.dirty() || this.unknown !== null || this.unsavedEdit || this.externals.size > this.refreshExternals
      || this.inFlight !== null || this.debounceTimer !== null || this.retryTimer !== null
  }

  /** The editor's text (null: none of its own; the queue's latestText is a
   *  refresh's pulled list, or nothing). */
  editorText(): string | null {
    return this.textRefreshOwned ? this.lastDurableText : this.currentText()
  }

  /**
   * "Keep my changes": a refresh's state that is SETTLED (no lost write of
   * its own still out) is dropped. Never an edit of the user's, never a write
   * that may still land.
   */
  dropRefreshState(): void {
    if (!this.textRefreshOwned || this.unknown || this.inFlight) return
    this.retireRefreshState()
    this.maybeSettle()
  }

  private retireRefreshState(): void {
    this.unknownRefreshOwned = false
    this.textRefreshOwned = false
    this.latestText = null
    this.unsavedEdit = false
    this.forceWrite = false
    // The row moved past the attempt: its rev is not known here any more (a
    // later instant save reads it fresh).
    this.lastDurableRev = undefined
    this.clearRetry()
    if (this.debounceTimer) { clearTimeout(this.debounceTimer); this.debounceTimer = null }
    // eslint-disable-next-line i18next/no-literal-string -- a queue phase
    this.phase = 'idle'
    this.fatalCode = null
    this.clearDraftNow()
    this.notify()
  }

  /** The editor's text: the newest edit, else the durable text. */
  currentText(): string | null {
    return this.latestText ?? this.lastDurableText
  }

  /** The editor's card_meta: the newest edit's, else the durable one (null: unknown). */
  currentMeta(): CardMetaMap | null {
    if (this.latestMeta) return this.latestMeta
    return this.durableMeta !== undefined ? parseCardMeta(this.durableMeta || null) : null
  }

  /**
   * What a write of `text` sends for card_meta, or undefined when it leaves
   * the column alone. Known stored value: the whole map pruned to the cards
   * `text` holds, when it differs. Unknown: a merge of the session's own
   * changes, only when there are some (pruning alone never writes).
   */
  metaWriteFor(text: string): MetaWrite | undefined {
    if (this.latestMeta === null) return undefined
    // Both sides only for the cards `text` holds: a card that left the list
    // is not an edit, and one an undo brings back is (its entry returns).
    const next = pruneCardMetaForText(this.latestMeta, text)
    const base = pruneCardMetaForText(this.metaBase, text)
    // UNKNOWN stored value: the session never saw the whole map, so it may
    // not prune the row's other entries (only its own delta is applied).
    if (canonicalCardMeta(next) !== canonicalCardMeta(base)) return this.noPrune ? { cardMetaMerge: { base, next }, cardMetaNoPrune: true } : { cardMetaMerge: { base, next } }
    // No change of the session's own: only a prune of what is stored (known,
    // and a session allowed to prune).
    if (this.durableMeta === undefined || this.noPrune) return undefined
    const stored = parseCardMeta(this.durableMeta || null)
    if (canonicalCardMeta(pruneCardMetaForText(stored, text)) === this.durableMeta) return undefined
    return { cardMetaMerge: { base: next, next } }
  }

  /** The session changed card_meta for the cards `text` holds. */
  private metaEdited(text: string): boolean {
    if (this.latestMeta === null) return false
    return canonicalCardMeta(pruneCardMetaForText(this.latestMeta, text)) !== canonicalCardMeta(pruneCardMetaForText(this.metaBase, text))
  }

  /**
   * At a session's open, with nothing pending: what the row's card_meta is
   * (undefined: UNKNOWN, merge mode) and the map the editor opened on.
   */
  setStoredMeta(stored: unknown, seed: CardMetaMap): void {
    if (this.hasPendingWork()) return
    this.durableMeta = stored !== undefined ? canonicalCardMeta(parseCardMeta(stored)) : undefined
    this.noPrune = stored === undefined
    this.metaBase = seed
    this.latestMeta = null
  }

  private dirty(): boolean {
    if (this.forceWrite) return true
    if (this.latestText !== null && this.latestText !== this.lastDurableText) return true
    return this.latestMeta !== null && this.metaWriteFor(this.latestText ?? this.lastDurableText ?? '') !== undefined
  }

  /** Text not yet durable, a lost write not yet resolved, or a write or
   *  timer still out. */
  hasPendingWork(): boolean {
    return this.dirty() || this.queuedOffline || this.unknown !== null || this.unsavedEdit || this.externals.size > 0
      || this.inFlight !== null || this.debounceTimer !== null || this.retryTimer !== null
  }

  /** There is something to send: newer text, or a lost write to resolve. */
  private needsWrite(): boolean {
    return this.unknown !== null || this.dirty()
  }

  private terminal(): boolean {
    return this.phase === 'stale' || this.phase === 'missing' || this.phase === 'fatal'
  }

  /** Whether leaving the page now would lose edits. */
  unloadArmed(): boolean {
    if (!this.hasPendingWork()) return false
    // Stopped on a failure: the draft keeps the edits, unless it could not
    // be written either.
    if (this.terminal()) return !this.draftPersisted
    return true
  }

  // ---- Attach / detach -----------------------------------------------------

  attach(args: QueueAttachArgs): number {
    this.token += 1
    this.attached = args
    this.lastArgs = args
    if (this.lastDurableDeck) args.applyPersistedDeck?.(this.lastDurableDeck)
    // A write that was held while nobody was attached goes now.
    if (this.needsWrite() && !this.inFlight && !this.terminal() && !this.debounceTimer) {
      this.clearRetry()
      this.scheduleWrite(0)
    }
    return this.token
  }

  detach(token: number): void {
    if (token !== this.token) return
    this.attached = null
    // Leaving with an edit still in its debounce: try it now, once (the hard
    // navigation case). A failure then waits for the next attach.
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = null
      if (!this.inFlight && this.needsWrite() && !this.terminal()) void this.write()
    }
    this.notify()
    this.maybeSettle()
  }

  private maybeSettle() {
    if (!this.attached && !this.hasPendingWork()) this.onSettled(this)
  }

  // ---- Edits ---------------------------------------------------------------

  /**
   * Record the editor's new text (and card_meta, when the editor has one;
   * undefined keeps the last): the draft is written NOW, the save soon.
   */
  /**
   * Returns whether the edit is safely recorded: false when it was REFUSED
   * (a fence is pending: nothing changes, the caller must keep any source
   * draft) or its draft could not be written. A caller deletes a source
   * draft only on true.
   */
  edit(text: string, meta?: CardMetaMap | null, opts?: { force?: boolean }): boolean {
    // While a Discard's fence is out, no document edit is taken.
    if (this.fenceOnly) return false
    // Read-only after a rejected session (C2): refused BEFORE anything
    // changes, the draft included.
    if (offlinePort && !offlinePort.canEdit(this.scope)) return false
    this.editGen += 1
    // An accepted uncertain recovery: a CAS write is owed even of the stored text.
    if (opts?.force) this.forceWrite = true
    this.latestText = text
    this.textRefreshOwned = false
    if (meta !== undefined) this.latestMeta = meta
    const wrote = this.writeDraft()
    if (!this.dirty()) {
      // Back to the stored text (an undo): nothing to save, UNLESS a write
      // of another text is still out, or a lost one is unresolved. Either can
      // still move the durable base, and this text would then need writing
      // back over it, so the draft ({base, text} equal for now) stays until
      // it settles, and the resolution keeps running.
      // Nor after a stale stop: the latest document is kept until Discard or
      // a successful save, whatever it equals.
      const keepDraft = !!this.inFlight || !!this.unknown || this.unsavedEdit || this.queuedOffline
      if (!keepDraft) this.clearDraftNow()
      if (this.debounceTimer) { clearTimeout(this.debounceTimer); this.debounceTimer = null }
      if (this.queuedOffline && !this.terminal()) {
        // The outbox still holds the opposite edit: the undo is recorded
        // there too (it clears the outbox, or becomes the successor).
        if (this.phase !== 'saving') this.phase = 'pending'
        this.scheduleWrite(SAVE_DEBOUNCE_MS)
      } else if (!this.inFlight && !this.terminal() && !this.unknown) {
        this.clearRetry()
        this.phase = 'idle'
      }
      this.notify()
      // Back on the stored text: recorded when no draft is needed (it was
      // rightly cleared), or the one that must be kept was written.
      return keepDraft ? wrote : true
    }
    if (!this.terminal() && this.phase !== 'retrying') {
      if (this.phase !== 'saving') this.phase = 'pending'
      this.scheduleWrite(SAVE_DEBOUNCE_MS)
    }
    this.notify()
    return wrote
  }

  // true only when THIS document's draft now holds the current text.
  private writeDraft(textIfNone?: string): boolean {
    // A fence is bookkeeping, never a recoverable document: the uncertainty
    // it settles belongs to the SOURCE draft, which stays (uncertain) until
    // the fence is saved. A journal here would shadow it on the next open.
    if (this.fenceOnly) return false
    const text = this.latestText ?? textIfNone
    if (text === undefined || text === null) return false
    try {
      // card_meta only when the session changed it, with its base; while a
      // write is uncertain the WHOLE map (an undo back to the base included:
      // the write out may have stored the value it replaced).
      const metaPart = this.uncertain && this.latestMeta
        ? { meta: this.latestMeta, metaBase: this.metaBase }
        : this.metaEdited(text) ? { meta: this.latestMeta!, metaBase: this.metaBase } : {}
      const draft: EditorDraft = {
        base: this.lastDurableText, text, ts: Date.now(),
        ...metaPart,
        ...(this.uncertain ? { uncertain: true as const } : {}),
      }
      const w = ownerStorage.write(draftKey(this.scope, this.deckId), JSON.stringify(draft))
      this.trackDraftCommit(w.accepted ? w.durable : false)
      return w.accepted
    } catch {
      this.trackDraftCommit(false)
      return false
    }
  }

  /** draftPersisted follows the newest draft write's DURABLE result (r2 #1). */
  private trackDraftCommit(durable: boolean | Promise<boolean>) {
    const seq = ++this.draftSeq
    if (typeof durable === 'boolean') {
      this.draftPersisted = durable
      this.draftCommitting = false
      return
    }
    this.draftPersisted = false
    this.draftCommitting = true
    void durable.then((ok) => {
      if (seq !== this.draftSeq) return
      this.draftPersisted = ok
      this.draftCommitting = false
      this.notify()
    })
  }

  private clearDraftNow() {
    clearDraftKey(draftKey(this.scope, this.deckId))
    this.draftSeq += 1
    this.draftCommitting = false
    this.draftPersisted = true
  }

  private scheduleWrite(ms: number) {
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null
      if (this.inFlight || this.terminal()) return
      // Nobody attached and not an explicit flush, or a re-rate holding the
      // queue: keep it (attach / the hold's release writes it).
      if (!this.attached || this.holds > 0) { this.notify(); return }
      void this.write()
    }, ms)
  }

  private clearRetry() {
    if (this.retryTimer) { clearTimeout(this.retryTimer); this.retryTimer = null }
  }

  private scheduleRetry() {
    this.clearRetry()
    if (!this.attached) return
    // Offline, retries belong to the sync scheduler (C1b): it calls retry()
    // when the API is reachable again (retryAllEditorSaves).
    if (offlinePort?.isOffline()) return
    this.retryAttempt += 1
    const delay = Math.min(BACKOFF_CAP_MS, BACKOFF_BASE_MS * 2 ** (this.retryAttempt - 1))
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null
      if (this.inFlight || this.terminal() || !this.needsWrite()) return
      if (!this.attached || this.holds > 0) { this.notify(); return }
      void this.write()
    }, delay)
  }

  // ---- The write -----------------------------------------------------------

  /** One attempt with the newest text. Resolves to the outcome status, or
   *  null when there was nothing to write or nothing to write through. */
  private write(): Promise<SaveOutcome['status'] | null> {
    if (this.inFlight) return this.inFlight
    // A lost write is resolved before anything newer goes.
    if (this.unknown) return this.resolve()
    const args = this.attached ?? this.lastArgs
    if (!args || (!this.dirty() && !this.queuedOffline)) return Promise.resolve(null)
    // Offline (or the outbox still holds this deck): recorded, not sent.
    const port = offlinePort
    if (port && !this.fenceOnly && !this.forceWrite && port.shouldQueue(this.scope, this.deckId)) return this.handOff(port)
    if (!this.dirty()) {
      // The outbox no longer holds anything for this deck: nothing to write.
      this.queuedOffline = false
      return Promise.resolve(null)
    }
    // A card_meta-only edit writes the durable text back with it.
    const text = (this.latestText ?? this.lastDurableText ?? '') as string
    const expected = this.lastDurableText
    this.phase = 'saving'
    this.notify()
    const run = (async (): Promise<SaveOutcome['status'] | null> => {
      let out: SaveOutcome
      // A command-zone change rides the SAME compare-and-swap write, derived
      // from the text itself (see zoneWriteFor), so a replay carries it too.
      // A fence sends neither (the hook writes only the list to its value).
      const fencing = this.fenceOnly
      const zone = fencing ? undefined : zoneWriteFor(text, expected)
      // And card_meta, when it moved (pruned to this text's cards).
      const meta = fencing ? undefined : this.metaWriteFor(text)
      // Journaled BEFORE it goes out: from here until its outcome is settled,
      // a reload finds the draft marked uncertain. (A fence of a list-less
      // row has no text to journal: its source draft stays the journal.)
      if (this.latestText === null && !fencing) this.latestText = text
      this.dispatched += 1
      this.writeDraft()
      try {
        out = await args.saveDeckChecked(args.analysis(), text, args.partner(), {
          existingId: this.deckId,
          updateOnly: true,
          rollbackOnFailure: true,
          expectedDecklist: expected,
          // The rev (and command zone) the base text was read with; unknown:
          // 'fresh' (the instant save establishes it against this base).
          expectedRev: this.lastDurableRev ?? 'fresh',
          ...(this.lastDurableZone ? { expectedCommandZone: this.lastDurableZone } : {}),
          keepAnalysis: true,
          bumpRecency: false,
          ...(zone ? { commandZone: zone } : {}),
          ...(meta ?? {}),
          // A forced write (an accepted uncertain recovery, a fence) exists to
          // CONSUME the base revision: saved only once an update committed.
          ...(this.forceWrite ? { mustConsumeRev: true } : {}),
          ...(fencing ? { fenceOnly: true } : {}),
        })
      } catch (cause) {
        // eslint-disable-next-line i18next/no-literal-string -- an outcome code
        out = { status: 'retryable_error', cause }
      }
      this.dispatched -= 1
      this.settle(out, text, meta)
      return out.status
    })()
    this.inFlight = run
    void run.finally(() => {
      if (this.inFlight === run) this.inFlight = null
      // Newer text arrived while this one was out: write it next.
      if (this.phase === 'pending' && this.dirty() && !this.debounceTimer) this.scheduleWrite(0)
      this.notify()
      this.maybeSettle()
    })
    return run
  }

  /**
   * Record the newest edit in the offline deck outbox (C4.6): one mutable
   * draft per deck, coalescing every offline edit; the scheduler builds the
   * guarded attempt online. The draft here stays the editor's journal.
   */
  private handOff(port: EditorOfflinePort): Promise<SaveOutcome['status'] | null> {
    const text = (this.latestText ?? this.lastDurableText ?? '') as string
    const meta = this.metaWriteFor(text)
    const gen = this.editGen
    this.phase = 'saving'
    this.notify()
    const run = (async (): Promise<SaveOutcome['status'] | null> => {
      let res: Awaited<ReturnType<EditorOfflinePort['recordEdit']>>
      try {
        res = await port.recordEdit({
          deckId: this.deckId,
          baseRev: this.lastDurableRev ?? null,
          baseText: this.lastDurableText,
          ...(this.durableMeta !== undefined ? { baseCardMeta: parseCardMeta(this.durableMeta || null) } : {}),
          text,
          cardMetaDelta: meta ? { base: meta.cardMetaMerge.base, next: meta.cardMetaMerge.next, ...(meta.cardMetaNoPrune ? { no_prune: true } : {}) } : null,
        })
      } catch {
        res = 'refused'
      }
      if (res === 'queued') {
        this.queuedOffline = true
        this.phase = 'offline'
        this.fatalCode = null
      } else if (res === 'cleared') {
        // Back on the acknowledged state with nothing pending in the outbox.
        this.queuedOffline = false
        this.fatalCode = null
        this.phase = this.dirty() ? 'pending' : 'idle'
        if (!this.dirty() && this.editGen === gen) this.clearDraftNow()
      } else if (res === 'read_only') {
        // "Sign in again to make changes": the edit stays in the draft.
        this.phase = 'fatal'
        this.fatalCode = 'auth'
      } else {
        this.phase = 'retrying'
        this.scheduleRetry()
      }
      // An edit made while recordEdit awaited (its debounce may have fired
      // and been dropped meanwhile) is handed off next, at once.
      if (this.editGen !== gen && (res === 'queued' || res === 'cleared')) this.phase = 'pending'
      return null
    })()
    this.inFlight = run
    void run.finally(() => {
      if (this.inFlight === run) this.inFlight = null
      if (this.phase === 'pending' && (this.dirty() || this.queuedOffline) && !this.debounceTimer) this.scheduleWrite(0)
      this.notify()
      this.maybeSettle()
    })
    return run
  }

  /**
   * The outbox landed this deck's edit (an ACKNOWLEDGED row). `cleared`: the
   * row holds everything the outbox had. A newer edit made since is written
   * next, against this row.
   */
  adoptOutboxRow(deck: SavedDeck, cleared: boolean): void {
    this.adoptStored(deck)
    if (this.durableMeta !== undefined && deck.cardMeta !== undefined) this.durableMeta = canonicalCardMeta(parseCardMeta(deck.cardMeta))
    if (cleared) {
      this.queuedOffline = false
      if (this.latestMeta !== null) this.metaBase = this.latestMeta
    }
    this.forceWrite = false
    this.unsavedEdit = false
    if (!this.dirty()) {
      if (!this.inFlight && this.phase !== 'stale' && this.phase !== 'missing') this.phase = 'idle'
      this.clearDraftNow()
    } else {
      this.writeDraft()
      if (!this.queuedOffline && !this.inFlight && !this.debounceTimer) {
        this.phase = 'pending'
        this.scheduleWrite(0)
      }
    }
    const target = this.attached
    target?.applyPersistedDeck?.(deck)
    target?.onDeckUpdated?.(deck)
    this.notify()
    this.maybeSettle()
  }

  /**
   * The outbox found the deck changed elsewhere (or deleted): the editor's
   * own "Your deck changed" / missing flow takes the edit over, its draft
   * kept. Nothing is written automatically.
   */
  outboxStopped(kind: 'stale' | 'missing'): void {
    this.queuedOffline = false
    this.clearRetry()
    if (this.debounceTimer) { clearTimeout(this.debounceTimer); this.debounceTimer = null }
    this.phase = kind
    this.unsavedEdit = this.latestText !== null
    this.writeDraft()
    this.notify()
  }

  /** Resolve the held lost write (same backoff, same single flight). */
  private resolve(): Promise<SaveOutcome['status'] | null> {
    if (this.inFlight) return this.inFlight
    const u = this.unknown
    const args = this.attached ?? this.lastArgs
    if (!u || !args) return Promise.resolve(null)
    this.phase = 'saving'
    this.notify()
    const run = (async (): Promise<SaveOutcome['status'] | null> => {
      let out: SaveOutcome
      try {
        out = await args.saveDeckChecked(args.analysis(), u.text, args.partner(), { existingId: this.deckId, resolveUnknown: u.attempt })
      } catch (cause) {
        out = { status: 'unknown', attempt: u.attempt, cause }
      }
      // Thrown away meanwhile (discard): only what the database says counts.
      if (this.unknown !== u) {
        if (out.status === 'saved') this.adoptStored(out.deck)
        if (!this.terminal()) this.phase = this.dirty() ? 'pending' : 'idle'
        return out.status
      }
      switch (out.status) {
        case 'saved':
        case 'stale':
        case 'missing':
          // Settled: the attempt is gone and the outcome is the write's own.
          this.unknown = null
          if (out.status !== 'saved' && this.unknownRefreshOwned && this.textRefreshOwned) {
            // A refresh's write that did NOT land, with no edit of the
            // user's since: nothing of the editor's is pending. Retired, so
            // it can never read as an unsaved edit (the pulled version stays
            // the deck's refresh choice); the next session starts from the row.
            this.retireRefreshState()
            this.retiredAs = out.status
            break
          }
          this.unknownRefreshOwned = false
          this.settle(out, u.text, u.meta)
          break
        case 'unknown':
          if (out.fatalCode) {
            // Its identical retry was rejected (signed out ...): stop, KEEP
            // the attempt; Try again resolves it first.
            this.phase = 'fatal'
            this.fatalCode = out.fatalCode
            break
          }
          this.phase = 'retrying'
          this.scheduleRetry()
          break
        case 'retryable_error':
          // Still unsettled: hold it and try again on the backoff.
          this.phase = 'retrying'
          this.scheduleRetry()
          break
        case 'fatal_error':
          // Stop; the attempt stays held and Try again resolves it first.
          this.phase = 'fatal'
          this.fatalCode = out.code
          break
      }
      return out.status
    })()
    this.inFlight = run
    void run.finally(() => {
      if (this.inFlight === run) this.inFlight = null
      if (this.phase === 'pending' && this.dirty() && !this.debounceTimer) this.scheduleWrite(0)
      this.notify()
      this.maybeSettle()
    })
    return run
  }

  // The stored row a resolution found, adopted as the durable base (no
  // phase change, no draft change).
  private adoptStored(deck: SavedDeck) {
    this.lastDurableText = deck.decklist ?? null
    this.lastDurableRev = deck.rev
    this.lastDurableZone = { commander: deck.commander, partner: deck.partner ?? null }
    this.lastDurableDeck = deck
  }

  /**
   * A full save made OUTSIDE the queue (a re-rate, a stored-list
   * re-analysis, a tool apply) for this deck, journaled like one of the
   * queue's own: the draft is marked uncertain BEFORE `save` is called (the
   * text it sends rides in the draft when the session has none), sign-out
   * and unload flushes wait for it, and its outcome settles the mark. A lost
   * response is held and resolved before anything newer (adoptUnknown).
   * `args` lets a controller nobody attached resolve it on a flush.
   */
  async runExternal(text: string, save: () => Promise<SaveOutcome>, args?: QueueAttachArgs, opts?: { refresh?: boolean }): Promise<SaveOutcome> {
    if (args && !this.lastArgs) this.lastArgs = args
    const refresh = !!opts?.refresh
    if (refresh) this.refreshExternals += 1
    this.dispatched += 1
    this.writeDraft(text)
    const run = (async (): Promise<SaveOutcome> => {
      try {
        return await save()
      } catch (cause) {
        return { status: 'retryable_error', cause }
      }
    })()
    this.externals.add(run)
    this.notify()
    let out: SaveOutcome
    try {
      out = await run
    } finally {
      this.externals.delete(run)
      this.dispatched -= 1
      if (refresh) this.refreshExternals -= 1
    }
    if (out.status === 'unknown') {
      this.adoptUnknown(out.attempt, text, { refresh })
    } else {
      if (out.status === 'saved' && this.latestText === null && !this.unknown && !this.inFlight) this.adoptStored(out.deck)
      // The journal: back to the session's own draft, or gone with none.
      if (this.latestText !== null) {
        if (this.dirty() || this.unsavedEdit || this.uncertain) this.writeDraft()
        else if (!this.inFlight) this.clearDraftNow()
      } else if (!this.uncertain) {
        this.clearDraftNow()
      }
    }
    this.notify()
    this.maybeSettle()
    return out
  }

  /**
   * A guarded save made OUTSIDE the queue (a re-rate) lost its response: the
   * queue holds and resolves it before writing anything newer, exactly like
   * one of its own. `text` is the decklist it sent.
   */
  adoptUnknown(attempt: UnknownSaveAttempt, text: string, opts?: { refresh?: boolean }): void {
    if (attempt.deckId !== this.deckId || this.unknown) return
    this.unknown = { attempt, text }
    this.unknownRefreshOwned = !!opts?.refresh
    if (this.latestText === null) {
      this.latestText = text
      this.textRefreshOwned = !!opts?.refresh
    }
    this.fatalCode = null
    this.phase = 'retrying'
    this.writeDraft()
    this.clearRetry()
    if (!this.inFlight && this.attached && this.holds === 0 && !this.debounceTimer) this.scheduleWrite(0)
    this.notify()
  }

  private settle(out: SaveOutcome, text: string, meta?: MetaWrite) {
    // Every outcome but 'unknown' settles the dispatched write (retryable
    // means it did not run): the uncertain mark goes (the dispatch count is
    // already down), and the draft is rewritten without it below.
    switch (out.status) {
      case 'saved': {
        // The list landed but card_meta did not (its guard was rejected and
        // it was never sent): the session's card_meta edits stay pending
        // (metaBase does not move, the draft keeps them) and the queue stops
        // on a failure instead of looping on a write that cannot land.
        const metaHeld = out.cardMetaHeld === true
        const sent = metaHeld ? undefined : meta
        // A fence wrote the list to its own value (null stays null).
        this.lastDurableText = this.fenceOnly ? (out.deck.decklist ?? null) : (out.deck.decklist ?? text)
        this.fenceOnly = false
        // The rev (and zone) that text was returned with: the next write's guard.
        this.lastDurableRev = out.deck.rev
        this.lastDurableZone = { commander: out.deck.commander, partner: out.deck.partner ?? null }
        // The owed write (an accepted uncertain recovery) landed; nothing is
        // left unsaved from an earlier stop.
        this.forceWrite = false
        this.unsavedEdit = false
        // What is stored now: what this write sent; else the row's own when
        // it says (a write without card_meta never changes the column).
        // A merge stays a merge: the session's map is not the whole stored
        // one, so its next edit is measured from what this one sent.
        // The next edit is measured from what this one sent; the row's own
        // card_meta (the merged result) is what is stored now.
        if (sent) {
          this.metaBase = sent.cardMetaMerge.next
          if (out.deck.cardMeta !== undefined) this.durableMeta = canonicalCardMeta(parseCardMeta(out.deck.cardMeta))
        } else if (this.durableMeta !== undefined && out.deck.cardMeta !== undefined) this.durableMeta = canonicalCardMeta(parseCardMeta(out.deck.cardMeta))
        this.lastDurableDeck = out.deck
        this.retryAttempt = 0
        this.clearRetry()
        // The draft follows in the SAME step: a reload from here must find
        // either nothing, or the newer text against the base that landed.
        if (metaHeld && this.dirty()) {
          this.phase = 'fatal'
          this.fatalCode = 'invalid'
          this.writeDraft()
        } else if (this.dirty()) {
          this.phase = 'pending'
          this.writeDraft()
        } else {
          this.phase = 'idle'
          this.clearDraftNow()
        }
        const target = this.attached
        target?.applyPersistedDeck?.(out.deck)
        target?.onDeckUpdated?.(out.deck)
        break
      }
      case 'stale':
      case 'missing': {
        const wasFence = this.fenceOnly
        this.fenceOnly = false
        // A refused fence leaves no document of its own (the source draft
        // is the one kept); nothing of the user's is unsaved here.
        if (wasFence) {
          this.phase = out.status
          break
        }
        // Stopped: the user's edit is KEPT (even an undo back to the obsolete
        // base: the row moved past it) and counts as unsaved work until it
        // is dismissed or saved.
        this.phase = out.status
        this.unsavedEdit = this.latestText !== null
        this.writeDraft()
        break
      }
      case 'fatal_error':
        this.phase = 'fatal'
        this.fatalCode = out.code
        this.rewriteDraft()
        break
      case 'retryable_error':
        this.phase = 'retrying'
        this.rewriteDraft()
        this.scheduleRetry()
        break
      case 'unknown':
        // The attempt may have landed: held EXACTLY as sent (never resent
        // against a newer rev) and resolved before anything newer goes. The
        // draft stays marked uncertain.
        this.unknown = { attempt: out.attempt, text, ...(meta ? { meta } : {}) }
        if (out.fatalCode) {
          this.phase = 'fatal'
          this.fatalCode = out.fatalCode
        } else {
          this.phase = 'retrying'
          this.scheduleRetry()
        }
        this.writeDraft()
        break
    }
  }

  // The draft without the uncertain mark: kept while it holds an edit, gone
  // when the editor is back on the stored text.
  private rewriteDraft() {
    if (this.latestText === null) return
    if (this.dirty() || this.unsavedEdit || this.uncertain) this.writeDraft()
    else this.clearDraftNow()
  }

  // ---- Explicit flush and recovery -----------------------------------------

  /**
   * Write everything now and wait for it: cancels the debounce (and any
   * backoff wait). Used before the editor closes, before a mid-edit Re-rate
   * and at sign-out. Resolves 'saved' when the database holds the editor's
   * text.
   */
  async flush(): Promise<FlushResult> {
    if (this.debounceTimer) { clearTimeout(this.debounceTimer); this.debounceTimer = null }
    this.clearRetry()
    let last: SaveOutcome['status'] | null = null
    for (let guard = 0; guard < 50; guard++) {
      // External full saves journaled on this deck settle first.
      if (this.externals.size > 0) {
        await Promise.all([...this.externals])
        continue
      }
      if (this.inFlight) {
        last = await this.inFlight
        // An already-running resolution that retired a refresh's own write:
        // that IS this flush's outcome too.
        if (this.retiredAs) {
          const as = this.retiredAs
          this.retiredAs = null
          return as
        }
        continue
      }
      if (this.phase === 'stale') return 'stale'
      if (this.phase === 'missing') return 'missing'
      if (this.phase === 'fatal') return 'fatal_error'
      if (this.unknown) {
        // Never saved while a lost write is unresolved: resolve it first.
        if (!this.attached && !this.lastArgs) return 'held'
        const r = await this.resolve()
        if (r === null) return 'held'
        if (this.unknown) return 'retryable_error'
        // A refresh's own write that did not land, retired: that IS the
        // outcome (nothing of the editor's is left to write).
        if (this.retiredAs) {
          const as = this.retiredAs
          this.retiredAs = null
          return as
        }
        // Resolved: this flush writes whatever is newer itself (the timer the
        // resolution armed for it is not needed).
        if (this.debounceTimer) { clearTimeout(this.debounceTimer); this.debounceTimer = null }
        continue
      }
      // The outbox holds this deck's edit: not saved yet (sign-out counts it).
      if (this.queuedOffline) return 'held'
      if (!this.dirty()) {
        this.notify()
        this.maybeSettle()
        return 'saved'
      }
      if (last === 'retryable_error') return 'retryable_error'
      if (!this.attached && !this.lastArgs) return 'held'
      last = await this.write()
      if (last === null) return 'held'
    }
    return 'retryable_error'
  }

  /**
   * FENCE before an uncertain recovery draft is discarded: a forced no-op
   * compare-and-swap of the row as this session loaded it (its text, at its
   * rev). Saved: the revision is consumed, so the older write the draft was
   * uncertain about can no longer land, and the draft may go. Anything else
   * (stale, unknown, a failure): the caller keeps the prompt. Only for a
   * session with no edit of its own (the prompt is gone once one is made).
   */
  async fence(): Promise<FlushResult> {
    // eslint-disable-next-line i18next/no-literal-string -- a flush result
    if (this.latestText !== null && this.latestText !== this.lastDurableText) return 'held'
    // Already stopped (stale / missing / fatal): the flush would return that
    // stop without writing, and a fenceOnly set here would never be cleared
    // by a write. Report the stop as is.
    if (this.phase === 'stale') return 'stale'
    if (this.phase === 'missing') return 'missing'
    // eslint-disable-next-line i18next/no-literal-string -- a flush result
    if (this.phase === 'fatal') return 'fatal_error'
    // No draft is journaled and no text adopted: the fence writes only the
    // list column to its own value (a list-less legacy row stays list-less),
    // and the source draft is the record until the fence is saved.
    this.fenceOnly = true
    this.forceWrite = true
    this.notify()
    return this.flush()
  }

  /** "Try again" after a fatal stop (or a backoff the user will not wait for). */
  retry(): void {
    if (this.phase === 'fatal' || this.phase === 'retrying') {
      this.phase = 'pending'
      this.fatalCode = null
      this.retryAttempt = 0
      this.clearRetry()
      this.notify()
      if (this.needsWrite()) this.scheduleWrite(0)
    }
  }

  /**
   * A mid-edit re-rate is saving the text it flushed (its own compare-and-swap
   * write): automatic writes wait until the returned release, so an edit made
   * while it runs never races that write. The edit is still recorded and its
   * draft written at once (edit()); it is saved after the release, against
   * the row the re-rate landed (markRerated). An explicit flush (page unload,
   * sign-out) still writes. The release is idempotent.
   */
  hold(): () => void {
    this.holds += 1
    let released = false
    return () => {
      if (released) return
      released = true
      this.holds -= 1
      if (this.holds > 0) return
      if (this.needsWrite() && !this.inFlight && !this.terminal() && !this.debounceTimer && this.attached) {
        this.clearRetry()
        this.scheduleWrite(0)
      }
      this.notify()
    }
  }

  /**
   * A full re-rate landed: its row is the new durable state AND the new
   * analysis. `ratedText` is the text the re-rate analyzed: when the editor
   * has moved past it meanwhile (an edit during the re-rate), that newer
   * text stays pending (its draft rebased onto the landed row) and is saved
   * next; otherwise nothing is pending and the draft goes.
   */
  markRerated(deck: SavedDeck, ratedText?: string): void {
    this.lastDurableText = deck.decklist ?? null
    this.lastDurableRev = deck.rev
    this.lastDurableZone = { commander: deck.commander, partner: deck.partner ?? null }
    this.lastDurableDeck = deck
    // The re-rate is itself a compare-and-swap write that landed.
    this.forceWrite = false
    // A full save never writes card_meta: the row keeps the one the flush
    // before it landed (the row's own, when it says).
    if (this.durableMeta !== undefined && deck.cardMeta !== undefined) this.durableMeta = canonicalCardMeta(parseCardMeta(deck.cardMeta))
    const textNewer = ratedText !== undefined && this.latestText !== null && this.latestText !== ratedText && this.latestText !== this.lastDurableText
    if (!textNewer) this.latestText = deck.decklist ?? null
    // A card_meta edit made while the re-rate ran is newer too.
    const metaNewer = this.latestMeta !== null && this.metaWriteFor(this.latestText ?? '') !== undefined
    if (!metaNewer) this.latestMeta = null
    const newer = textNewer || metaNewer
    if (this.debounceTimer) { clearTimeout(this.debounceTimer); this.debounceTimer = null }
    this.clearRetry()
    this.fatalCode = null
    if (newer) {
      this.phase = 'pending'
      this.writeDraft()
      if (this.holds === 0 && this.attached) this.scheduleWrite(0)
    } else {
      this.phase = 'idle'
      this.clearDraftNow()
    }
    this.notify()
    this.maybeSettle()
  }

  /**
   * A settled controller whose durable text the store has moved past (another
   * tab or device): adopt the stored text. Ignored while work is out.
   */
  rebase(stored: string | null, storedCardMeta?: unknown, storedRev?: number, storedZone?: { commander: string | null; partner: string | null }): void {
    if (this.hasPendingWork()) return
    this.lastDurableText = stored
    // The rev and zone read with `stored` (undefined: unknown).
    this.lastDurableRev = storedRev
    this.lastDurableZone = storedZone
    if (storedCardMeta !== undefined) this.durableMeta = canonicalCardMeta(parseCardMeta(storedCardMeta))
    this.latestText = null
    this.textRefreshOwned = false
    this.latestMeta = null
    this.lastDurableDeck = null
    if (!this.terminal()) this.phase = 'idle'
    this.notify()
  }

  /** Throw the session away (Discard / Use current): nothing pending. */
  discard(): void {
    // The offline outbox's copy of this deck's edit goes with it.
    if (this.queuedOffline || offlinePort?.shouldQueue(this.scope, this.deckId)) void offlinePort?.discard(this.deckId)
    this.queuedOffline = false
    if (this.debounceTimer) { clearTimeout(this.debounceTimer); this.debounceTimer = null }
    this.clearRetry()
    this.latestText = null
    this.latestMeta = null
    // A held lost write goes too: the user chose the row as stored. (A
    // resolution already out only updates the durable base, see resolve.)
    this.unknown = null
    this.unknownRefreshOwned = false
    this.textRefreshOwned = false
    this.forceWrite = false
    this.fenceOnly = false
    this.unsavedEdit = false
    if (!this.inFlight) this.phase = 'idle'
    this.fatalCode = null
    this.clearDraftNow()
    this.notify()
    this.maybeSettle()
  }

  /** Test seam: drop timers so a test's fake clock cannot leak. */
  dispose(): void {
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    this.clearRetry()
    this.debounceTimer = null
  }
}

// ---- Registry ----------------------------------------------------------------

type PersistedSink = (deck: SavedDeck, scope: string, source: unknown) => void
const persistedSinks = new Set<PersistedSink>()

/** useSavedDecks listens here, so its list follows every landed edit. */
export function subscribePersistedDecks(fn: PersistedSink): () => void {
  persistedSinks.add(fn)
  return () => { persistedSinks.delete(fn) }
}

/**
 * A guarded write that landed (any typed save with updateOnly +
 * expectedDecklist: the queue's instant saves, re-rates, re-analyses): every
 * OTHER mounted deck list of the scope hears it, so the same tab never shows
 * two versions of one deck.
 */
export function broadcastPersisted(scope: string, deck: SavedDeck, source?: unknown): void {
  for (const sink of [...persistedSinks]) sink(deck, scope, source)
}

// Guarded writes in flight, per scope. A deck-list load waits for these
// before it reads, so it always reads AFTER any write that was already out
// when it started (no replay cache, no ordering guesswork).
const inFlightWrites = new Map<string, Set<Promise<unknown>>>()

/** Register a guarded write for its scope until it settles. */
export function trackGuardedWrite<T>(scope: string, write: Promise<T>): Promise<T> {
  let set = inFlightWrites.get(scope)
  if (!set) {
    set = new Set()
    inFlightWrites.set(scope, set)
  }
  const entry = write.then(() => undefined, () => undefined)
  set.add(entry)
  void entry.then(() => {
    set!.delete(entry)
    if (set!.size === 0 && inFlightWrites.get(scope) === set) inFlightWrites.delete(scope)
  })
  return write
}

/**
 * Resolves once every guarded write in flight for the scope NOW has settled,
 * capped at `capMs` (an offline write retrying for longer must not hold the
 * list hostage; its draft and stale handling cover it). null when nothing is
 * in flight, so a caller can stay synchronous.
 */
export function pendingGuardedWrites(scope: string, capMs = 10_000): Promise<void> | null {
  const set = inFlightWrites.get(scope)
  if (!set || set.size === 0) return null
  const all = Promise.all([...set]).then(() => undefined)
  return Promise.race([all, new Promise<void>(resolve => setTimeout(resolve, capMs))])
}

const registry = new Map<string, EditorSaveController>()
let unloadInstalled = false

const regKey = (scope: string, deckId: string) => `${scope}|${deckId}`

function installUnloadGuard() {
  if (unloadInstalled || typeof window === 'undefined') return
  unloadInstalled = true
  // The guard lives here, not in a component, so it holds after the screen
  // that made the edit is gone.
  // eslint-disable-next-line i18next/no-literal-string -- a DOM event name
  window.addEventListener('beforeunload', (e: BeforeUnloadEvent) => {
    if (![...registry.values()].some(c => c.unloadArmed())) return
    e.preventDefault()
    // Legacy browsers only show the prompt when returnValue is set.
    e.returnValue = ''
  })
  // The page is going: one last attempt for anything pending.
  // eslint-disable-next-line i18next/no-literal-string -- a DOM event name
  window.addEventListener('pagehide', () => {
    for (const c of registry.values()) if (c.hasPendingWork()) void c.flush()
  })
}

/**
 * The controller for this (scope, deck): the live one when it exists (its
 * durable text is newer than any row a remount just loaded), else a new one
 * seeded from the stored list.
 */
export function getEditorSaveController(init: {
  scope: string
  deckId: string
  storedDecklist: string | null
  /** The row's card_meta as loaded (raw). Absent: unknown. */
  storedCardMeta?: unknown
  /** The row's rev, read with storedDecklist. Absent: unknown. */
  storedRev?: number
  /** The row's commander / partner, read with storedDecklist. */
  storedZone?: { commander: string | null; partner: string | null }
}): EditorSaveController {
  installUnloadGuard()
  const key = regKey(init.scope, init.deckId)
  const existing = registry.get(key)
  if (existing) return existing
  const c = new EditorSaveController({
    ...init,
    onSettled: ctl => {
      if (registry.get(key) === ctl) registry.delete(key)
    },
  })
  registry.set(key, c)
  return c
}

/** The live controller, without creating one. */
export function peekEditorSaveController(scope: string, deckId: string): EditorSaveController | undefined {
  return registry.get(regKey(scope, deckId))
}

/**
 * Flush every controller of one user, within `timeoutMs`. ok only when every
 * edit is durable; anything still pending, stopped or failed is not.
 * useAuth.signOut runs this BEFORE revoking the session.
 */
export async function flushScope(scope: string, opts: { timeoutMs: number }): Promise<{ ok: boolean }> {
  const controllers = [...registry.values()].filter(c => c.scope === scope && c.hasPendingWork())
  if (controllers.length === 0) return { ok: true }
  let timer: ReturnType<typeof setTimeout> | null = null
  const timeout = new Promise<'timeout'>(resolve => {
    // eslint-disable-next-line i18next/no-literal-string -- a sentinel
    timer = setTimeout(() => resolve('timeout'), opts.timeoutMs)
  })
  const all = Promise.all(controllers.map(c => c.flush()))
  const res = await Promise.race([all, timeout])
  if (timer) clearTimeout(timer)
  if (res === 'timeout') return { ok: false }
  return { ok: res.every(r => r === 'saved') }
}

/** The API is reachable again (C1 recovered): every stopped retry resumes now. */
export function retryAllEditorSaves(): void {
  for (const c of registry.values()) if (c.getSnapshot().phase === 'retrying') c.retry()
}

/** Test seam. */
export function __resetEditorSaveQueues(): void {
  for (const c of registry.values()) c.dispose()
  registry.clear()
  inFlightWrites.clear()
}
